
































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import AdminBrands from "@/mixins/adminBrands";
import CustomFieldsMixin from "@/mixins/customFieldsMixin";
import TagsMixin from "@/mixins/tagsMixin";
import MultilevelCategoriesFetcher from "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesFetchMixin";
import ContractProductsFiltersMixins from "@/mixins/provisioning/contractProductsFiltersMixins";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { FilterOperators, FilterTypes } from "@/data/table";
import { CustomFieldsMajorTypes } from "@/data/enums/customFields";

// Filters
import {
  CProdFilters,
  CProdClientsFilters
} from "@/data/filters/contractProducts";
import clientFilters from "@/data/filters/client";
import {
  orderFilters,
  orderItemsFilters,
  orderClientsFilters
} from "@/data/filters/order";

// Types

import { IState } from "@/store";
import { ISegmentTypes } from "@/data/enums/segments";
import { ISegment, ISegmentForm } from "@/models/segments";
import { IFilter } from "@/models/tables";
import { IBrand } from "@/models/brands";
import { UUID } from "@/data/enums";

@Component({
  components: {
    "segment-params": () =>
      import("@/components/app/admin/segments/segmentParams.vue")
  },
  computed: {
    ...mapState({
      segment(state: IState, getters) {
        return getters[`data/single`]({
          id: this.segmentId,
          storeModule: DataModules.SEGMENTS
        });
      }
    }),
    ...mapGetters({
      brandId: "brand/id",
      isOrgMode: "brand/isMultibrand"
    })
  }
})
export default class AddEditSegmentModal extends Mixins(
  RequestPromises,
  TagsMixin,
  CustomFieldsMixin,
  AdminBrands,
  MultilevelCategoriesFetcher,
  ContractProductsFiltersMixins,
  HasValidation
) {
  public declare $refs: {
    name: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Getters/State
  public declare brandId: IBrand["id"];
  public isOrgMode!: boolean;
  public segment!: ISegment;

  // Props
  @Prop({ default: "" })
  public segmentId!: ISegment["id"];

  @Prop({ default: null })
  public segmentType!: ISegmentTypes;

  @Prop({ default: "create" })
  public actionType!: string;

  @Prop({ default: "" })
  public newSegmentRedirectRoute!: string;

  @Prop({ default: () => ({}) })
  public filter!: { [key: string]: string };

  // Variables
  public form = {
    name: "",
    type: this.segmentType,
    params: {
      brand_id: this.brandId,
      filter: this.filter
    }
  } as ISegmentForm;
  public formClone = {} as ISegmentForm;

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;
  public segmentParamsReady = false;

  public created() {
    if (!this.contentAvailable) return;
    this.form.params.brand_id = this.isOrgMode ? "" : this.brandId;
    this.isLoading = true;
    this.loadData();
  }

  public get isDisabled() {
    return (
      this.isProcessing ||
      !(this.segmentExists ? this.canUpdateSegment : this.canCreateSegment)
    );
  }

  public get isOneTimePurchase() {
    return this.segmentType === ISegmentTypes.CONTRACTS_PRODUCTS_SALE;
  }

  public get segmentFilters(): IFilter[] {
    switch (this.segmentType) {
      case ISegmentTypes.CLIENT:
        return this.$_.concat(
          this.$_.cloneDeep(clientFilters()),
          [
            {
              key: "tags.id",
              label: this.$t("_.tag"),
              operator: FilterOperators.ALL,
              group: "client",
              type: FilterTypes.TAG,
              options: this.tags
            }
          ],
          this.customFieldsFilters(
            CustomFieldsMajorTypes.CLIENT,
            "",
            "client_custom_fields",
            true
          )
        );
      case ISegmentTypes.CONTRACTS_PRODUCTS:
      case ISegmentTypes.CONTRACTS_PRODUCTS_SALE:
      case ISegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION:
        return this.$_.concat(
          this.getProductCategoryFilledFilters(
            CProdFilters(),
            true,
            this.isOneTimePurchase
          ),
          this.createProvisionFilters(this.isOneTimePurchase),
          CProdClientsFilters(),
          [
            this.createTagFilter(
              "clients.tags.id",
              this.$t("_.client_tag"),
              "client"
            )
          ],
          this.customFieldsFilters(
            CustomFieldsMajorTypes.CLIENT,
            "",
            "client_custom_fields",
            true
          )
        );
      case ISegmentTypes.ORDER:
        return this.$_.concat(
          orderFilters(),
          this.customFieldsFilters(
            CustomFieldsMajorTypes.BASKET,
            "",
            "order_custom_fields",
            true
          ),
          this.getProductCategoryFilledFilters(orderItemsFilters(), false),
          [
            this.createTagFilter(
              "products.tags.id",
              this.$t("_.order_item_tag"),
              "order_item"
            )
          ],
          orderClientsFilters(),
          [
            this.createTagFilter(
              "client.tags.id",
              this.$t("_.client_tag"),
              "client"
            )
          ],
          this.customFieldsFilters(
            CustomFieldsMajorTypes.CLIENT,
            "client",
            "client_custom_fields",
            true
          )
        );
      default:
        return [];
    }
  }

  public async loadData() {
    try {
      // Check provisioning first
      await this.getOrgProvisioning(this.isReloading);

      await Promise.all(this.getTableFiltersData());

      this.fillForm();
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    }

    this.isReloading = false;
    this.isLoading = false;

    this.$nextTick(() => {
      if (
        this.$_.has(this.$refs, "name") &&
        this.$_.isEmpty(this.$_.get(this.form, "params.filter", {}))
      ) {
        this.$refs.name.focus();
      }
    });
  }

  public getTableFiltersData() {
    const allPromises = [
      this.getClientCustomFields(this.isReloading),
      this.getTags(this.isReloading),
      this.getSegment(this.isReloading)
    ];

    if (this.segmentType === ISegmentTypes.ORDER) {
      allPromises.push(this.getBasketCustomFields(this.isReloading));
      allPromises.push(this.getRootCategories(this.isReloading));
    }

    if (
      this.segmentType === ISegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION ||
      this.segmentType === ISegmentTypes.CONTRACTS_PRODUCTS_SALE ||
      this.segmentType === ISegmentTypes.CONTRACTS_PRODUCTS
    ) {
      allPromises.push(this.getRootCategories(this.isReloading));

      if (this.hasProvisioning) {
        allPromises.push(this.getFiltersData(this.isReloading));
      }
    }

    return allPromises;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getSegment(ignoreStored = false) {
    if (!this.segmentExists) {
      return Promise.resolve();
    }
    return this.$store.dispatch(`data/${DataModules.SEGMENTS}/get`, {
      id: this.segmentId,
      vm: this,
      ignoreStored
    });
  }

  public fillForm() {
    if (this.segmentExists) {
      this.form = this.$_.merge(
        {},
        this.form,
        this.$_.pick(this.segment, ["name", "type", "params"])
      );
    }

    if (this.$_.get(this.form, "params.filter", []).length === 0) {
      this.$set(this.form.params, "filter", {});
    }

    this.formClone = this.$_.cloneDeep(this.form);
  }

  public get brandsWithoutOrg() {
    return this.$_.filter(this.brands, brand => brand.id !== UUID.ORG);
  }

  public get canCreateSegment() {
    return this.$userCan("create_segment");
  }

  public get canUpdateSegment() {
    return this.$userCan("update_segment");
  }

  public get canGetSegment() {
    return this.$userCan("get_segment");
  }

  public get segmentExists() {
    return !!this.segmentId;
  }

  public get guardFunctionalities() {
    return this.$_.concat(
      ["list_client_fields"],
      this.segmentExists
        ? ["get_segment", "update_segment"]
        : ["create_segment"]
    );
  }

  public get contentAvailable() {
    return (
      this.$userCan("list_client_fields") &&
      (this.segmentExists
        ? this.canGetSegment && this.canUpdateSegment
        : this.canCreateSegment)
    );
  }

  public get formIsValid() {
    if (this.$_.isEmpty(this.form.name)) {
      return false;
    }

    if (this.$_.isEqual(this.form, this.formClone)) {
      return false;
    }

    return true;
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.formIsValid ||
      this.hasErrors ||
      !this.segmentParamsReady ||
      this.isLoading
    );
  }

  public onValidFilterParams(newParam: boolean) {
    this.segmentParamsReady = newParam;
  }

  public validateBeforeSubmit() {
    this.isProcessing = true;
    this.validate()
      .then(result => {
        if (result) {
          return this.segmentExists
            ? this.updateSegment()
            : this.createSegment();
        }
        throw { message: this.$t("_sentence.form_is_not_valid") };
      })
      .then(result => {
        this.$store.dispatch("toast/show", {
          message: this.$t(
            `_sentence.segment_${this.segmentExists ? "updated" : "created"}`
          )
        });

        if (!this.segmentExists && !!this.newSegmentRedirectRoute) {
          this.$router.push({
            name: this.newSegmentRedirectRoute,
            params: {
              segmentId: result.id
            }
          });
        }
        this.$emit(
          this.segmentExists ? "segment-updated" : "segment-created",
          result
        );

        this.$emit("close");
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public createSegment() {
    return this.$store.dispatch(`data/${DataModules.SEGMENTS}/create`, {
      data: this.form
    });
  }

  public updateSegment() {
    return this.$store.dispatch(`data/${DataModules.SEGMENTS}/update`, {
      id: this.segmentId,
      data: this.form
    });
  }

  public onParamsChanged(newParamsFilter) {
    this.form.params.filter = this.$_.cloneDeep(newParamsFilter);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.SEGMENTS,
      vm: this
    });
  }
}
